import {
  CalendarOutlined,
  HistoryOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Alert,
  App,
  Button,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Row,
  Select,
  Table,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { AccrualRow } from "@/entities/accrual";
import { useAccrualHistory } from "@/entities/accrual";
import { useUser, useUsers } from "@/entities/user";
import { useManualAccrual } from "@/features/manual-accrual";
import { useRollDay } from "@/features/roll-day";
import { errorMessage } from "@/shared/api";
import { SECTIONS } from "@/shared/config";
import { formatDate, formatMoney, formatPercent } from "@/shared/lib";
import { EmptyState, Money, PageHeader, SectionCard } from "@/shared/ui";

interface ManualValues {
  user_robot_id: number;
  date: Dayjs;
  percent: string;
  comment?: string;
}

/** Прокрутка дня (AD-05): одна кнопка, но двигает балансы всем сразу. */
function RollDayCard() {
  const { t } = useTranslation();
  const { modal, message } = App.useApp();
  const roll = useRollDay();

  return (
    <SectionCard
      color={SECTIONS.accruals.color}
      icon={<CalendarOutlined aria-hidden />}
      title={t("accruals.rollTitle")}
    >
      <Typography.Paragraph type="secondary">{t("accruals.rollHint")}</Typography.Paragraph>
      <Button
        type="primary"
        icon={<ThunderboltOutlined aria-hidden />}
        loading={roll.isPending}
        onClick={() => {
          void modal.confirm({
            title: t("accruals.roll"),
            content: t("accruals.rollConfirm"),
            okText: t("accruals.roll"),
            cancelText: t("common.cancel"),
            onOk: async () => {
              const result = await roll.mutateAsync();
              // `created === 0` означает «за эту дату уже начислено»: сработала
              // идемпотентность по (user_robot_id, date), а не ошибка.
              if (result.created === 0) {
                void message.info(
                  t("accruals.rollNothing", { date: formatDate(result.date) }),
                );
                return;
              }
              void message.success(
                t("accruals.rollDone", {
                  date: formatDate(result.date),
                  created: result.created,
                  amount: formatMoney(result.total_amount, { sign: "always" }),
                }),
              );
            },
          });
        }}
      >
        {t("accruals.roll")}
      </Button>
    </SectionCard>
  );
}

/** Ручное начисление: процент конкретному роботу на конкретную дату. */
function ManualCard({ userId }: { userId: number | null }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<ManualValues>();
  const user = useUser(userId ?? 0, userId !== null);
  const mutation = useManualAccrual(userId);

  const robots = userId === null ? [] : (user.data?.robots ?? []);

  return (
    <SectionCard
      color={SECTIONS.accruals.color}
      icon={<ThunderboltOutlined aria-hidden />}
      title={t("accruals.manualTitle")}
    >
      <Typography.Paragraph type="secondary">{t("accruals.manualHint")}</Typography.Paragraph>

      {userId === null ? (
        <Alert type="info" showIcon title={t("accruals.noUser")} />
      ) : (
        <Form<ManualValues>
          form={form}
          layout="vertical"
          onFinish={(values) => {
            mutation.mutate(
              {
                user_robot_id: values.user_robot_id,
                date: values.date.format("YYYY-MM-DD"),
                percent: String(values.percent),
                ...(values.comment ? { comment: values.comment } : {}),
              },
              {
                onSuccess: (result) => {
                  // Оператор обязан увидеть, что введённые им −500 % превратились
                  // в −100 %: иначе он решит, что начисление прошло как задумано.
                  if (result.clamped) {
                    void message.warning(
                      t("accruals.manualClamped", {
                        requested: formatPercent(String(values.percent)),
                        percent: formatPercent(result.percent),
                      }),
                    );
                  } else {
                    void message.success(
                      t("accruals.manualDone", {
                        amount: formatMoney(result.amount, { sign: "always" }),
                        percent: formatPercent(result.percent, { sign: "always" }),
                      }),
                    );
                  }
                  form.resetFields(["percent", "comment"]);
                },
              },
            );
          }}
        >
          <Form.Item
            name="user_robot_id"
            label={t("accruals.manualRobot")}
            rules={[{ required: true, message: t("login.required") }]}
          >
            <Select
              placeholder={t("accruals.manualRobot")}
              options={robots.map((robot) => ({
                value: robot.id,
                label: `${robot.robot_name} · ${formatMoney(robot.purchase_price)}`,
                disabled: robot.status !== "active",
              }))}
            />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="date"
                label={t("accruals.manualDate")}
                rules={[{ required: true, message: t("login.required") }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="percent"
                label={t("accruals.manualPercent")}
                rules={[{ required: true, message: t("login.required") }]}
              >
                <InputNumber stringMode step="0.1" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Button
            type="primary"
            htmlType="submit"
            icon={<ThunderboltOutlined aria-hidden />}
            loading={mutation.isPending}
          >
            {t("accruals.manualSubmit")}
          </Button>
        </Form>
      )}
    </SectionCard>
  );
}

function HistoryCard({ userId }: { userId: number | null }) {
  const { t } = useTranslation();
  const { data, isFetching, isError, error } = useAccrualHistory(userId);

  const columns: ColumnsType<AccrualRow> = [
    {
      title: t("common.date"),
      key: "date",
      width: 130,
      render: (_, row) => formatDate(row.date),
    },
    { title: t("users.robot"), dataIndex: "robot_name" },
    {
      title: t("accruals.percent"),
      key: "percent",
      align: "right",
      width: 130,
      render: (_, row) => (
        <Typography.Text
          strong
          style={{
            color:
              Number(row.percent) < 0 ? "var(--ant-color-error)" : "var(--ant-color-success)",
          }}
        >
          {formatPercent(row.percent, { sign: "always" })}
        </Typography.Text>
      ),
    },
    {
      title: t("common.amount"),
      key: "amount",
      align: "right",
      render: (_, row) => <Money value={row.amount} sign="always" colored />,
    },
  ];

  return (
    <SectionCard
      color={SECTIONS.accruals.color}
      icon={<HistoryOutlined aria-hidden />}
      title={t("accruals.historyTitle")}
    >
      {isError && (
        <Alert type="error" showIcon title={errorMessage(error)} style={{ marginBottom: 16 }} />
      )}
      <Table<AccrualRow>
        rowKey="id"
        pagination={{ pageSize: 20, showSizeChanger: false }}
        columns={columns}
        dataSource={data ?? []}
        loading={isFetching}
        locale={{
          emptyText: (
            <EmptyState
              icon={userId === null ? <UserOutlined aria-hidden /> : <HistoryOutlined aria-hidden />}
              title={userId === null ? t("accruals.noUser") : t("accruals.emptyHistory")}
              hint={userId === null ? t("accruals.historyHint") : null}
            />
          ),
        }}
      />
    </SectionCard>
  );
}

export function AccrualsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState<number | null>(null);
  const users = useUsers(search, null);

  const options = (users.data?.pages.flatMap((page) => page.items) ?? []).map((user) => ({
    value: user.id,
    label: `${user.username ? `@${user.username}` : (user.first_name ?? `#${String(user.id)}`)} · ${String(user.telegram_id)}`,
  }));

  return (
    <>
      <PageHeader
        section="accruals"
        title={t("accruals.title")}
        subtitle={t("accruals.subtitle")}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <RollDayCard />
        </Col>
        <Col xs={24} lg={12}>
          <SectionCard
            color={SECTIONS.users.color}
            icon={<UserOutlined aria-hidden />}
            title={t("accruals.manualUser")}
          >
            <Typography.Paragraph type="secondary">
              {t("accruals.historyHint")}
            </Typography.Paragraph>
            <Select
              showSearch
              allowClear
              style={{ width: "100%" }}
              placeholder={t("accruals.manualUserPlaceholder")}
              filterOption={false}
              onSearch={setSearch}
              onChange={(value: number | undefined) => {
                setUserId(value ?? null);
              }}
              options={options}
              loading={users.isFetching}
              notFoundContent={users.isFetching ? null : t("common.empty")}
            />
          </SectionCard>
        </Col>
        <Col xs={24} lg={12}>
          <ManualCard userId={userId} />
        </Col>
        <Col xs={24} lg={12}>
          <HistoryCard userId={userId} />
        </Col>
      </Row>
    </>
  );
}
